import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { projects, products } from '../data/sbbData';
import { MapPin, Calendar, User, Briefcase, ChevronRight, ShieldAlert, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function ProjectDetail() {
  const { projectSlug } = useParams();
  const { lang, t } = useLanguage();

  const project = projects.find((p) => p.slug === projectSlug) || projects[0];
  const relatedProjects = projects.filter((p) => p.slug !== project.slug).slice(0, 2);

  return (
    <div className="project-detail-page">
      {/* Hero Header */}
      <PageHero eyebrow="Studi Kasus Proyek" title={project.name} subtitle={project.summary} image={project.image} />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><Link to="/pelanggan-proyek">{t('nav_projects')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{project.name}</span></li>
          </ul>
        </div>
      </div>

      {/* Content Details */}
      <section className="section">
        <div className="container">
          <div className="grid-2">
            {/* Left Column: Narrative (Challenge & Solution) */}
            <div>
              <span className="accent-bar"></span>
              <h2 className="section-title" style={{ fontSize: '28px' }}>Tantangan & Solusi Rekayasa</h2>
              
              <div className="narrative-box challenge">
                <div className="narrative-header">
                  <ShieldAlert size={20} color="var(--color-brand-primary)" />
                  <h3>Tantangan Proyek Lapangan</h3>
                </div>
                <p>{project.challenge}</p>
              </div>

              <div className="narrative-box solution">
                <div className="narrative-header">
                  <CheckCircle size={20} color="var(--color-success)" />
                  <h3>Solusi & Implementasi SBB</h3>
                </div>
                <p>{project.solution}</p>
              </div>

              {/* Used Products Badges */}
              {project.usedProducts && (
                <div className="used-products-wrapper">
                  <h3 className="sub-title">Produk & Material Yang Digunakan:</h3>
                  <div className="prod-badge-chips">
                    {project.usedProducts.map((prodName, i) => (
                      <span key={i} className="prod-badge-chip">{prodName}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Project Metadata Box & Gallery */}
            <div>
              <div className="project-meta-card">
                <h3 className="meta-card-title">Informasi Proyek</h3>
                <div className="meta-item">
                  <User size={18} className="icon-red" />
                  <div>
                    <strong>Pemilik Proyek (Client):</strong>
                    <div>{project.client}</div>
                  </div>
                </div>
                <div className="meta-item">
                  <Briefcase size={18} className="icon-red" />
                  <div>
                    <strong>Kontraktor Utama:</strong>
                    <div>{project.contractor}</div>
                  </div>
                </div>
                <div className="meta-item">
                  <MapPin size={18} className="icon-red" />
                  <div>
                    <strong>Lokasi Proyek:</strong>
                    <div>{project.location}</div>
                  </div>
                </div>
                <div className="meta-item">
                  <Calendar size={18} className="icon-red" />
                  <div>
                    <strong>Tahun Pelaksanaan:</strong>
                    <div>{project.year}</div>
                  </div>
                </div>
              </div>

              {/* Gallery Preview */}
              {project.gallery && (
                <div className="gallery-box">
                  <h4 style={{ marginBottom: '12px' }}>Dokumentasi Lapangan</h4>
                  <div className="gallery-grid">
                    {project.gallery.map((imgUrl, i) => (
                      <img key={i} src={imgUrl} alt={`Dokumentasi ${i}`} className="gallery-img" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="section section-subtle">
        <div className="container">
          <div className="section-header">
            <span className="accent-bar"></span>
            <h2 className="section-title">{t('related_projects')}</h2>
          </div>

          <div className="grid-2">
            {relatedProjects.map((p) => (
              <div key={p.slug} className="sbb-card">
                <img src={p.image} alt={p.name} className="sbb-card-img" />
                <div className="sbb-card-body">
                  <span className="sbb-card-tag">{p.location} • {p.year}</span>
                  <h3 className="sbb-card-title">{p.name}</h3>
                  <p className="sbb-card-desc">{p.summary}</p>
                  <Link to={`/proyek/${p.slug}`} className="btn btn-secondary btn-sm">
                    <span>Lihat Studi Kasus</span>
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .narrative-box {
          padding: 24px;
          border-radius: var(--border-radius-md);
          margin-bottom: 24px;
          border: 1px solid var(--color-border);
        }

        .narrative-box.challenge {
          background-color: #fff8f8;
          border-left: 4px solid var(--color-brand-primary);
        }

        .narrative-box.solution {
          background-color: #f6fbf8;
          border-left: 4px solid var(--color-success);
        }

        .narrative-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .narrative-header h3 {
          font-size: 16px;
          font-weight: 700;
          color: var(--color-ink);
        }

        .used-products-wrapper {
          margin-top: 32px;
        }

        .prod-badge-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 12px;
        }

        .prod-badge-chip {
          background-color: var(--color-brand-primary);
          color: #ffffff;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .project-meta-card {
          background-color: var(--color-surface-subtle);
          padding: 28px;
          border-radius: var(--border-radius-md);
          border: 1px solid var(--color-border);
          margin-bottom: 24px;
        }

        .meta-card-title {
          font-size: 18px;
          font-weight: 800;
          color: var(--color-ink);
          margin-bottom: 20px;
          border-bottom: 2px solid var(--color-brand-primary);
          padding-bottom: 8px;
        }

        .meta-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .meta-item strong {
          color: var(--color-ink);
          display: block;
        }

        .gallery-box {
          margin-top: 24px;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .gallery-img {
          width: 100%;
          height: 140px;
          object-fit: cover;
          border-radius: var(--border-radius-sm);
        }
      `}</style>
    </div>
  );
}
